/**
 * useSpawnRecipes Hook
 *
 * CRUD operations for spawn_recipes table.
 * Spawn recipes define the exact composition of a spawn bag
 * (grain weight, water weight, bag size, LC volume per bag).
 *
 * Fixes the grain deduction bug where bag_weight_kg was used
 * instead of actual grain_weight_kg.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch active spawn recipes
 * @param {Object} options
 * @param {boolean} options.activeOnly - Only fetch active recipes (default: true)
 * @returns {Object} { recipes, loading, error, refetch }
 */
export function useSpawnRecipes(options = {}) {
  const [recipes, setRecipes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { activeOnly = true } = options

  const fetchRecipes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('spawn_recipes')
        .select('*')
        .order('name')

      if (activeOnly) {
        query = query.eq('is_active', true)
      }

      const { data, error: fetchError } = await query

      if (fetchError) throw fetchError

      setRecipes(data || [])
    } catch (err) {
      console.error('[useSpawnRecipes] Error:', err)
      setError(err.message)
      setRecipes([])
    } finally {
      setLoading(false)
    }
  }, [activeOnly])

  useEffect(() => {
    fetchRecipes()
  }, [fetchRecipes])

  return { recipes, loading, error, refetch: fetchRecipes }
}

/**
 * Hook for spawn recipe mutations (create, update, deactivate)
 * @returns {Object} Mutation functions and state
 */
export function useSpawnRecipeMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const createRecipe = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const { data: recipe, error: insertError } = await supabase
        .from('spawn_recipes')
        .insert({
          name: data.name,
          grain_type: data.grain_type,
          grain_weight_kg: data.grain_weight_kg,
          water_weight_kg: data.water_weight_kg,
          bag_weight_kg: data.bag_weight_kg,
          lc_volume_ml: data.lc_volume_ml ?? 10,
          use_filters: data.use_filters ?? true,
          is_active: true
        })
        .select()
        .single()

      if (insertError) throw insertError
      return recipe
    } catch (err) {
      console.error('[useSpawnRecipeMutations.createRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const updateRecipe = useCallback(async (id, updates) => {
    setLoading(true)
    setError(null)

    try {
      const updateData = {}
      if (updates.name !== undefined) updateData.name = updates.name
      if (updates.grain_type !== undefined) updateData.grain_type = updates.grain_type
      if (updates.grain_weight_kg !== undefined) updateData.grain_weight_kg = updates.grain_weight_kg
      if (updates.water_weight_kg !== undefined) updateData.water_weight_kg = updates.water_weight_kg
      if (updates.bag_weight_kg !== undefined) updateData.bag_weight_kg = updates.bag_weight_kg
      if (updates.lc_volume_ml !== undefined) updateData.lc_volume_ml = updates.lc_volume_ml
      if (updates.use_filters !== undefined) updateData.use_filters = updates.use_filters
      if (updates.is_active !== undefined) updateData.is_active = updates.is_active

      const { data: recipe, error: updateError } = await supabase
        .from('spawn_recipes')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (updateError) throw updateError
      return recipe
    } catch (err) {
      console.error('[useSpawnRecipeMutations.updateRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const deactivateRecipe = useCallback(async (id) => {
    setLoading(true)
    setError(null)

    try {
      const { error: updateError } = await supabase
        .from('spawn_recipes')
        .update({ is_active: false })
        .eq('id', id)

      if (updateError) throw updateError
    } catch (err) {
      console.error('[useSpawnRecipeMutations.deactivateRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return {
    loading,
    error,
    clearError,
    createRecipe,
    updateRecipe,
    deactivateRecipe
  }
}

export default useSpawnRecipes
